/**
 * Dónde va la navegación de una aplicación publicada: en una columna al lado
 * del documento, o en una barra arriba con un menú que abre el cajón de las
 * páginas, igual que en un teléfono.
 *
 * Es una preferencia de quien mira, no del que hizo la aplicación, y se
 * recuerda por aplicación. Vive aparte del componente porque la decide la
 * aplicación publicada y el sidebar solo recibe el resultado.
 */

export type NavLayout = "sidebar" | "topbar";

/** Clave del navegador donde se recuerda la forma de la navegación de una aplicación. */
const layoutKey = (appId: string) => `plane_nav_layout_${appId}`;

interface NavLayoutState {
  readonly layout: NavLayout;
  toggle: () => void;
}

/** Empieza en columna salvo que se haya pedido arriba a propósito. */
export function navLayout(appId: () => string): NavLayoutState {
  let layout = $state<NavLayout>(remembered(appId()));

  // Cambiar de aplicación trae la preferencia de la nueva, no la de la vieja.
  let last = appId();
  $effect(() => {
    const id = appId();
    if (id === last) return;
    last = id;
    layout = remembered(id);
  });

  $effect(() => {
    try {
      localStorage.setItem(layoutKey(appId()), layout);
    } catch {
      /* sin memoria del navegador, vuelve a empezar en columna */
    }
  });

  return {
    get layout() {
      return layout;
    },
    toggle() {
      layout = layout === "topbar" ? "sidebar" : "topbar";
    },
  };
}

/** La forma que se dejó la última vez. Sin almacenamiento, en columna. */
function remembered(appId: string): NavLayout {
  try {
    return localStorage.getItem(layoutKey(appId)) === "topbar" ? "topbar" : "sidebar";
  } catch {
    return "sidebar";
  }
}
